import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";

export function PurgeDataButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/purge-data");
      const d = data.deleted;
      toast.success(`Cleared ${d.users} accounts, ${d.projects} projects, ${d.reviews} reviews`);
      setOpen(false);
      setConfirm("");
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button data-testid="clear-data-btn" onClick={() => setOpen(true)} variant="ghost"
        className="rounded-full border border-destructive/40 text-destructive hover:bg-destructive/10">
        <Trash2 className="mr-2 h-4 w-4" /> Clear all data
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirm(""); }}>
        <DialogContent className="glass-panel max-w-md space-y-4 rounded-2xl border-destructive/30 text-white">
          <DialogTitle className="flex items-center gap-2 font-display text-xl text-destructive">
            <AlertTriangle className="h-5 w-5" /> Clear all data
          </DialogTitle>
          <p className="text-sm text-zinc-400">
            This permanently deletes <span className="text-white">all client & employee accounts, bookings,
            projects, messages, reviews and payouts</span>. Your admin account is kept. This cannot be undone.
          </p>
          <p className="text-sm text-zinc-300">Type <span className="font-mono text-destructive">DELETE</span> to confirm:</p>
          <Input data-testid="clear-data-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="DELETE" className="rounded-sm border-white/10 bg-black" />
          <Button data-testid="clear-data-submit" onClick={purge} disabled={busy || confirm !== "DELETE"}
            className="w-full rounded-full bg-destructive text-white hover:bg-destructive/90 disabled:opacity-40">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Permanently delete everything
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
